import {
  FormAsyncAutocomplete,
  FormStaticAutocomplete,
  FormTextField,
  Icon,
  PageContainer,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { mdiContentSaveOutline, mdiExclamation } from "@mdi/js";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
} from "@mui/material";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { Material } from "../../../../../@types/services/Material";
import type { PlantField } from "../../../../../@types/services/PlantField";
import type { RequesterArea } from "../../../../../@types/services/RequesterArea";
import { MaterialNumberField } from "../../../../../components/molecules/MaterialNumberField";
import { qualityDeviationOptions } from "../../../../../constants/mmRequest/qualityDeviationOptions";
import { quantityOfMaterialOptions } from "../../../../../constants/mmRequest/quantityOfMaterialOptions";
import { ForbiddenError } from "../../../../../errors/ForbiddenError";
import {
  fetchDuimpRequest,
  updateDuimpRequest,
} from "../../../../../services/duimpRequests";
import { fetchPlantFields } from "../../../../../services/plantFields";
import { fetchRequesterAreas } from "../../../../../services/requesterAreas";

type FormValues = {
  requester_area: RequesterArea;
  requester_field: PlantField;
  quantity_of_materials: string;
  matnumber: string[];
  subject: string;
  description: string;
  quality_deviation: string[];
  quality_deviation_description: string;
};

const EditDuimpRequest: React.FC = () => {
  const { t } = useTranslation();

  const { duimpRequestId } = Route.useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [formData, setFormData] = useState<FormValues | null>(null);
  const [mmRequestId, setMmRequestId] = useState<number | null>(null);

  const { handleSubmit, control, reset, setValue, watch } = useForm<FormValues>(
    {
      defaultValues: {
        description: "",
        matnumber: [],
        quality_deviation: [],
        quality_deviation_description: "",
        quantity_of_materials: "",
        requester_area: {} as RequesterArea,
        requester_field: {} as PlantField,
        subject: "",
      },
    },
  );

  const requesterField = watch("requester_field");
  const title = mmRequestId
    ? `${t("common.duimpRequest")} #${mmRequestId}`
    : t("common.duimpRequest");

  const handleClose = useCallback(() => {
    setOpenConfirmDialog(false);
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!formData || !mmRequestId) return;

    handleClose();
    setLoadingSubmit(true);

    const formattedData = {
      mm_request_attributes: {
        description: formData.description,
        id: mmRequestId.toString(),
        matnumber: formData.matnumber.map((material) =>
          typeof material === "string"
            ? material
            : (material as Material).matnumber,
        ),
        quality_deviation: formData.quality_deviation,
        quality_deviation_description: formData.quality_deviation_description,
        quantity_of_materials: formData.quantity_of_materials,
        requester_area: formData.requester_area.name,
        requester_field: formData.requester_field.name,
        subject: formData.subject,
      },
    };

    await updateDuimpRequest(duimpRequestId, formattedData)
      .then(() => {
        enqueueSnackbar(t("duimpRequests.requestUpdated"), {
          variant: "success",
        });
        navigate({
          params: { duimpRequestId },
          to: "/vbuyer/duimp_requests/$duimpRequestId",
        });
      })
      .catch(() => {
        setLoadingSubmit(false);
        enqueueSnackbar(t("duimpRequests.errorMessage"), {
          variant: "error",
        });
      });
  }, [formData, handleClose, duimpRequestId, navigate, mmRequestId, t]);

  const onSubmit: SubmitHandler<FormValues> = useCallback(async (data) => {
    setFormData(data);
    setOpenConfirmDialog(true);
  }, []);

  useEffect(() => {
    const loadData = async (): Promise<void> => {
      try {
        if (duimpRequestId) {
          const duimpRequest = await fetchDuimpRequest(duimpRequestId);

          setMmRequestId(duimpRequest.mm_request.id);
          reset({
            description: duimpRequest.mm_request.description || "",
            matnumber: duimpRequest.mm_request.matnumber || [],
            quality_deviation: duimpRequest.mm_request.quality_deviation || [],
            quality_deviation_description:
              duimpRequest.mm_request.quality_deviation_description || "",
            quantity_of_materials:
              duimpRequest.mm_request.quantity_of_materials || "",
            requester_area: duimpRequest.mm_request.requester_area || "",
            requester_field: duimpRequest.mm_request.requester_field || "",
            subject: duimpRequest.mm_request.subject || "",
          });
        }
      } catch {
        enqueueSnackbar(t("duimpRequests.errorMessage"), {
          variant: "error",
        });
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [duimpRequestId, reset, t]);

  if (loading) {
    return (
      <PageContainer
        backTo={`/vbuyer/duimp_requests/${duimpRequestId}`}
        maxWidth="md"
        showBackButton
        title={title}
      >
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      </PageContainer>
    );
  }

  return (
    <>
      <PageContainer
        backTo={`/vbuyer/duimp_requests/${duimpRequestId}`}
        maxWidth="md"
        showBackButton
        title={title}
      >
        <Paper
          sx={{
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
            flexShrink: 0,
            gap: "16px",
            padding: "24px",
          }}
        >
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                width: "100%",
              }}
            >
              <FormAsyncAutocomplete
                control={control}
                fetchOptions={fetchPlantFields}
                filters={(searchTerm) => ({
                  active_on_mm_request: true,
                  enabled: true,
                  name: searchTerm,
                })}
                getOptionLabel={(option) => option.name || ""}
                isOptionEqualToValue={(option, value) =>
                  typeof value !== "string" && option.id === value.id
                }
                label={t("duimpRequests.requesterField")}
                name="requester_field"
                queryKey={["requesterFields", "infinite"]}
                required
                rules={{
                  required: t("duimpRequests.requesterFieldRequiredMessage"),
                }}
              />

              <FormAsyncAutocomplete
                control={control}
                disabled={!requesterField}
                fetchOptions={fetchRequesterAreas}
                filters={(searchTerm) => ({
                  enabled: true,
                  name: searchTerm,
                  plant_field_name: requesterField ? requesterField.name : "",
                })}
                getOptionLabel={(option) => option.name || ""}
                isOptionEqualToValue={(option, value) =>
                  typeof value !== "string" && option.id === value.id
                }
                label={t("duimpRequests.requesterArea")}
                name="requester_area"
                queryKey={["requesterAreas", "infinite"]}
              />

              <FormStaticAutocomplete
                control={control}
                getOptionLabel={(option) => option}
                label={t("duimpRequests.quantityOfMaterials")}
                name="quantity_of_materials"
                options={quantityOfMaterialOptions}
              />

              <MaterialNumberField
                control={control}
                helperText={t("duimpRequests.materialHelper")}
                label={t("duimpRequests.material")}
                name="matnumber"
                rules={{ required: t("duimpRequests.requiredMessage") }}
                setValue={setValue}
              />

              <FormTextField
                control={control}
                helperText={t("mmRequests.subjectHelper")}
                label={t("duimpRequests.subject")}
                name="subject"
              />

              <FormTextField
                control={control}
                helperText={t("mmRequests.descriptionHelper")}
                label={t("duimpRequests.description")}
                multiline
                name="description"
                rows={6}
              />

              <FormStaticAutocomplete
                control={control}
                getOptionLabel={(option) =>
                  option ? t(`mmRequests.qualityDeviation.${option}`) : ""
                }
                label={t("duimpRequests.qualityDeviation")}
                multiple
                name="quality_deviation"
                options={qualityDeviationOptions}
              />

              <FormTextField
                control={control}
                label={t("duimpRequests.qualityDeviationDescription")}
                multiline
                name="quality_deviation_description"
                rows={3}
              />

              <Button
                color="success"
                disabled={loadingSubmit}
                startIcon={
                  loadingSubmit ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : (
                    <Icon icon={mdiContentSaveOutline} />
                  )
                }
                type="submit"
                variant="contained"
              >
                {t("common.save")}
              </Button>
            </Box>
          </form>
        </Paper>
      </PageContainer>
      <Dialog
        aria-describedby="alert-dialog-description"
        aria-labelledby="alert-dialog-title"
        onClose={handleClose}
        open={openConfirmDialog}
        slotProps={{
          paper: {
            sx: {
              pt: 4,
              px: 2,
              textAlign: "center",
              width: "600px",
            },
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mb: 3,
            mt: -1,
          }}
        >
          <Box
            sx={{
              alignItems: "center",
              backgroundColor: "white",
              border: "3px solid",
              borderColor: "#ED6C02",
              borderRadius: "50%",
              color: "#ED6C02",
              display: "flex",
              height: "80px",
              justifyContent: "center",
              width: "80px",
            }}
          >
            <Icon fontSize="large" icon={mdiExclamation} />
          </Box>
        </Box>

        <DialogTitle sx={{ textAlign: "center" }}>
          {t("duimpRequests.modalTitle")}
        </DialogTitle>

        <DialogContent>
          <DialogContentText
            sx={{ textAlign: "center", whiteSpace: "pre-line" }}
          >
            {t("duimpRequests.modalMessage")}
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>{t("cancel")}</Button>
          <Button autoFocus onClick={handleConfirm}>
            {t("ok")}
          </Button>
        </DialogActions>
      </Dialog>
      {!!loadingSubmit && (
        <Dialog open>
          <DialogContent sx={{ p: 4, textAlign: "center" }}>
            <CircularProgress />
            <DialogContentText sx={{ mt: 2 }}>
              {t("duimpRequests.dialog.processing")}
            </DialogContentText>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export const Route = createFileRoute(
  "/_private/vbuyer/duimp_requests/$duimpRequestId/edit",
)({
  beforeLoad({ context }) {
    if (context.auth.user?.roles_names.includes("Edit material requests")) {
      return;
    }

    throw new ForbiddenError();
  },
  component: EditDuimpRequest,
});
